import { FileVersionTitle } from "@/components/features/files/FileVersionTitle";
import { downloadFileVersion } from "@/lib/data/requests/files/canonical-files";
import { handleDownload } from "@/lib/utils/matterFile";
import {
  FileViewer,
  type FilePreviewVersion,
} from "@/components/features/matter/file-repo/FileViewer";
import {
  ActionConfirmationModal,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  ScrollHint,
  Typography,
} from "@/components/ui";
import { FileBulkActions } from "@/components/ui/FileBulkActions";
import { useUser } from "@/components/UserProvider";
import {
  useCreateFirmFile,
  useDeleteFirmFile,
  useUpdateFirmFile,
} from "@/lib/data/react-query";
import { retrieveFirmFilesOptions } from "@/lib/data/react-query/firm/firm-file";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { FirmFile } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  getFirmFileDisplayName,
  getFirmFileDisplayNamesInFolder,
  getFirmFileExtension,
  isFirmFolderDescendant,
  type FirmFileSortKey,
} from "@/lib/utils/firmFile";
import { getFileIcon, getFileNameParts } from "@/lib/utils/matterFile";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import Fuse from "fuse.js";
import {
  Download,
  EllipsisVertical,
  File,
  FilesIcon,
  FileText,
  Folder,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import type { DragEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { AddOrEditFirmFileModal } from "../AddOrEditFirmFileModal";
import { CreateDocxFirmFileModal } from "../CreateDocxFirmFileModal";
import { EditDocxFirmFileModal } from "../EditDocxFirmFileModal";
import { EditPdfFirmFileModal } from "../EditPdfFirmFileModal";
import { FirmFolderTree } from "../FirmFolderTree";
import { FirmFileContextMenu } from "./FirmFileContextMenu";
import { downloadFirmFile } from "./firmFileDownload";
import { useFirmFilePreview } from "./useFirmFilePreview";

function FirmFilesViewDangerous() {
  const { hasPagePermission, hasActionPermission } = useUser();

  const canView = hasPagePermission(PAGE_PERMISSION_KEYS.firmFiles);
  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canDocumentEdit = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.update,
  );

  const {
    data,
    isLoading,
    refetch: refetchFirmFiles,
  } = useQuery(retrieveFirmFilesOptions(canView));
  const files = useMemo(() => data?.data ?? [], [data?.data]);

  const createFirmFile = useCreateFirmFile();
  const updateFirmFile = useUpdateFirmFile();
  const deleteFirmFile = useDeleteFirmFile();

  const multiFileInputRef = useRef<HTMLInputElement>(null);

  const [currentFolderId, setCurrentFolderId] = useState("");
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [versionControlsContainer, setVersionControlsContainer] =
    useState<HTMLDivElement | null>(null);
  const [previewVersion, setPreviewVersion] =
    useState<FilePreviewVersion | null>(null);
  const [sortKey] = useState<FirmFileSortKey>("dateModified");
  const [isCreateSlideoverOpen, setIsCreateSlideoverOpen] = useState(false);
  const [createMode, setCreateMode] = useState<"file" | "folder">("file");
  const [itemBeingEdited, setItemBeingEdited] = useState<
    FirmFile | undefined
  >();
  const [isEditSlideoverOpen, setIsEditSlideoverOpen] = useState(false);
  const [isCreateDocxOpen, setIsCreateDocxOpen] = useState(false);
  const [docxEditorFile, setDocxEditorFile] = useState<FirmFile | null>(null);
  const [pdfEditorFile, setPdfEditorFile] = useState<FirmFile | null>(null);
  const [autoOpenNewFileUpload, setAutoOpenNewFileUpload] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<FirmFile | undefined>();
  const [bulkItemsToDelete, setBulkItemsToDelete] = useState<FirmFile[] | null>(
    null,
  );
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set(),
  );

  const currentFolder = useMemo(
    () => files.find((file) => file.id === currentFolderId && file.isFolder),
    [currentFolderId, files],
  );

  const selectedFile = useMemo(
    () => files.find((file) => file.id === activeFileId && !file.isFolder),
    [activeFileId, files],
  );

  const selectedItem = selectedFile ?? currentFolder;

  useEffect(() => {
    if (
      currentFolderId &&
      !files.some((file) => file.id === currentFolderId && file.isFolder)
    ) {
      setCurrentFolderId("");
    }
  }, [currentFolderId, files]);

  useEffect(() => {
    if (
      activeFileId &&
      !files.some((file) => file.id === activeFileId && !file.isFolder)
    ) {
      setActiveFileId(null);
    }
  }, [activeFileId, files]);

  useEffect(() => {
    setSelectedItemIds((prev) => {
      const validIds = new Set(
        files.filter((file) => !file.isFolder).map((file) => file.id),
      );
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [files]);

  const searchMatches = useMemo(() => {
    const sanitizedQuery = query.trim();
    if (!sanitizedQuery) return null;

    const fuse = new Fuse(files, {
      keys: [
        { name: "name", weight: 0.85 },
        { name: "description", weight: 0.15 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
    });

    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [files, query]);

  const treeItems = useMemo(() => {
    if (!searchMatches) {
      return files;
    }

    const byId = new Map(files.map((item) => [item.id, item] as const));
    const included = new Map<string, FirmFile>();

    const addWithAncestors = (item: FirmFile) => {
      included.set(item.id, item);
      let parentId = item.idLocation || "";

      while (parentId) {
        const parent = byId.get(parentId);
        if (!parent) break;
        included.set(parent.id, parent);
        parentId = parent.idLocation || "";
      }
    };

    searchMatches.forEach(addWithAncestors);
    return Array.from(included.values());
  }, [files, searchMatches]);

  const isUploadingFiles = createFirmFile.isPending;
  const showDropOverlay = canCreate && (isDragActive || isUploadingFiles);
  const selectedFiles = useMemo(
    () =>
      files.filter((file) => !file.isFolder && selectedItemIds.has(file.id)),
    [files, selectedItemIds],
  );

  const openCreateSlideover = (
    mode: "file" | "folder",
    options?: { autoOpenUpload?: boolean },
  ) => {
    setCreateMode(mode);
    setAutoOpenNewFileUpload(Boolean(options?.autoOpenUpload));
    setIsCreateSlideoverOpen(true);
  };

  const handleSetCreateOpen = (open: boolean) => {
    setIsCreateSlideoverOpen(open);
    if (!open) {
      setAutoOpenNewFileUpload(false);
    }
  };

  const handleMultiFileUpload = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;

    Object.values(fileList).forEach((file) => {
      createFirmFile.mutate({
        name: getFileNameParts(file.name).fileName,
        idLocation: currentFolderId,
        isFolder: false,
        file,
      });
    });
  };

  const dragContainsFiles = (event: DragEvent<HTMLDivElement>) => {
    const types = event.dataTransfer.types;
    return Array.from(types).includes("Files");
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!canCreate || !dragContainsFiles(event)) return;
    setIsDragActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    if (!canCreate || !dragContainsFiles(event)) return;
    setIsDragActive(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) return;
    setIsDragActive(false);
  };

  const handleDropFiles = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!canCreate || !dragContainsFiles(event)) return;
    setIsDragActive(false);
    handleMultiFileUpload(event.dataTransfer.files);
  };

  const moveItemToFolder = async (item: FirmFile, targetFolderId: string) => {
    if ((item.idLocation || "") === targetFolderId) {
      return;
    }

    if (item.isFolder) {
      if (item.id === targetFolderId) {
        toast.error("Cannot move a folder into itself.");
        return;
      }

      if (isFirmFolderDescendant(files, item.id, targetFolderId)) {
        toast.error("Cannot move a folder into one of its descendants.");
        return;
      }
    }

    const latestFirmFiles = await refetchFirmFiles();
    const latestItem =
      latestFirmFiles.data?.data.find((file) => file.id === item.id) ?? item;

    await updateFirmFile.mutateAsync({
      id: latestItem.id,
      dateModified: latestItem.dateModified,
      idLocation: targetFolderId || null,
    });
  };

  const moveSelectedItemsToFolder = async (
    draggedItem: FirmFile,
    targetFolderId: string,
  ) => {
    const itemsToMove = selectedItemIds.has(draggedItem.id)
      ? selectedFiles
      : [draggedItem];

    for (const item of itemsToMove) {
      await moveItemToFolder(item, targetFolderId);
    }
  };

  const handleSelectRoot = () => {
    setCurrentFolderId("");
    setActiveFileId(null);
  };

  const handleSelectFolder = (folderId: string) => {
    setCurrentFolderId(folderId);
    setActiveFileId(null);
  };

  const handleSelectFile = (item: FirmFile) => {
    setActiveFileId(item.id);
    setCurrentFolderId(item.idLocation || "");
  };

  const getDescendantFileIds = (folderId: string) => {
    const ids = new Set<string>();
    const stack = [folderId];

    while (stack.length > 0) {
      const currentId = stack.pop();
      if (!currentId) continue;

      files.forEach((file) => {
        if ((file.idLocation || "") !== currentId) return;

        if (file.isFolder) {
          stack.push(file.id);
        } else {
          ids.add(file.id);
        }
      });
    }

    return Array.from(ids);
  };

  const handleToggleSelection = (item: FirmFile, checked: boolean) => {
    const idsToUpdate = item.isFolder
      ? getDescendantFileIds(item.id)
      : [item.id];

    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      idsToUpdate.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const handleToggleRootSelection = (checked: boolean) => {
    const fileIds = files
      .filter((file) => !file.isFolder)
      .map((file) => file.id);

    setSelectedItemIds(checked ? new Set(fileIds) : new Set());
  };

  const handleOpenEditSlideover = () => {
    if (!selectedItem) return;
    setItemBeingEdited(selectedItem);
    setIsEditSlideoverOpen(true);
  };
  const handleEditItem = (item: FirmFile) => {
    setItemBeingEdited(item);
    setIsEditSlideoverOpen(true);
  };

  const handleDownloadFirmFile = async (file: FirmFile) => {
    if (!(await downloadFirmFile(file))) {
      toast.error("This file is not available to download.");
    }
  };
  const handleDownloadSelectedItem = async () => {
    if (!selectedFile) return;
    const versionId =
      previewVersion?.fileId === selectedFile.id
        ? previewVersion.versionId
        : null;
    if (versionId) {
      try {
        const result = await downloadFileVersion(selectedFile.id, versionId);
        await handleDownload(result.fileName, result.url, true);
      } catch {
        toast.error("Unable to download this version. Please try again.");
      }
      return;
    }
    await handleDownloadFirmFile(selectedFile);
  };
  const handleCreateSubfolder = (folder: FirmFile) => {
    handleSelectFolder(folder.id);
    openCreateSlideover("folder");
  };

  const handleDownloadFiles = async (items: FirmFile[]) => {
    if (items.length === 0) {
      toast.error("No selected files are available to download.");
      return;
    }

    try {
      await Promise.all(
        items.filter((file) => file.presignedUrlKey).map(downloadFirmFile),
      );
    } catch (error) {
      console.error("Failed to download selected files", error);
      toast.error("Unable to download selected files. Please try again.");
    }
  };

  const handleClearSelection = () => {
    setSelectedItemIds(new Set());
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || selectedItemIds.size === 0) return;
      handleClearSelection();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedItemIds]);

  const handleConfirmBulkDelete = async () => {
    if (!bulkItemsToDelete || bulkItemsToDelete.length === 0) return;

    const items = bulkItemsToDelete;
    setBulkItemsToDelete(null);

    const results = await Promise.allSettled(
      items.map((item) =>
        deleteFirmFile.mutateAsync({
          id: item.id,
          isFolder: item.isFolder,
        }),
      ),
    );

    const failures = results.filter((result) => result.status === "rejected");
    if (failures.length > 0) {
      toast.error(`Failed to delete ${failures.length} file(s).`);
    } else {
      toast.success("Deleted selected files.");
    }

    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      items.forEach((item) => next.delete(item.id));
      return next;
    });

    if (activeFileId && items.some((item) => item.id === activeFileId)) {
      setActiveFileId(null);
    }
  };

  const {
    canEditSelectedDocx,
    canEditSelectedPdf,
    handleOpenDocxEditor,
    handleOpenPdfEditor,
    isSelectedDocx,
    isSelectedPdf,
    previewFile,
  } = useFirmFilePreview({
    selectedFile,
    canUpdate,
    canDocumentEdit,
    setDocxEditorFile,
    setPdfEditorFile,
  });

  const headerTitle = selectedItem
    ? getFirmFileDisplayName(selectedItem)
    : "Firm Files";

  const HeaderIcon = selectedItem
    ? selectedItem.isFolder
      ? Folder
      : getFileIcon(getFirmFileDisplayName(selectedItem))
    : Folder;

  const canEditSelectedItem = canUpdate && Boolean(selectedItem);
  const canDeleteSelectedItem = canDelete && Boolean(selectedItem);
  const canDownloadSelectedItem = canView && Boolean(previewFile);
  const canUploadToCurrentLocation = canCreate;
  const showBulkActions = selectedFiles.length > 0;
  if (!canView) return null;

  return (
    <>
      <div className="w-full min-w-0 overflow-x-hidden">
        <div className="ml-2 gap-y-1">
          <Typography color="primary" variant="heading">
            Firm files
          </Typography>
        </div>

        <div className="bg-card mt-4 flex h-[680px] min-h-[680px] max-w-full min-w-0 overflow-hidden rounded-md border">
          <div className="border-border bg-card flex w-[360px] max-w-[360px] min-w-[360px] shrink-0 flex-col border-r">
            <div className="border-b px-4 py-4">
              {canCreate && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      openCreateSlideover("folder");
                    }}
                    type="button"
                  >
                    <Folder className="mr-2 h-4 w-4" />
                    New folder
                  </Button>
                  <div className="flex items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-r-none border-r-0"
                      onClick={() => {
                        openCreateSlideover("file", {
                          autoOpenUpload: true,
                        });
                      }}
                      type="button"
                    >
                      <File className="mr-2 h-4 w-4" />
                      Upload file
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="rounded-l-none px-2"
                          size="sm"
                          title="Upload options"
                          type="button"
                        >
                          {createFirmFile.isPending ? (
                            <LoadingSpinner />
                          ) : (
                            <EllipsisVertical className="h-4 w-4" />
                          )}
                          <span className="sr-only">Upload options</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setIsCreateDocxOpen(true);
                          }}
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Create file
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            if (multiFileInputRef.current) {
                              multiFileInputRef.current.value = "";
                              multiFileInputRef.current.click();
                            }
                          }}
                        >
                          <FilesIcon className="mr-2 h-4 w-4" />
                          Bulk upload
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <input
                      type="file"
                      ref={multiFileInputRef}
                      className="hidden"
                      multiple
                      onChange={(event) => {
                        handleMultiFileUpload(event.target.files);
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="mt-3">
                <Input
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                  }}
                  placeholder="Search files..."
                  className="w-full"
                />
              </div>
            </div>

            {isLoading ? (
              <div className="flex min-h-0 flex-1 items-center justify-center p-6">
                <LoadingSpinner />
              </div>
            ) : (
              <ScrollHint
                showOnTop
                className="min-h-0 flex-1"
                contentClassName="h-full min-h-0 overflow-y-auto p-2"
                fadeClassName="from-black/20 to-transparent"
                hintClassName="hidden"
              >
                <FirmFolderTree
                  files={treeItems}
                  currentFolderId={currentFolderId}
                  selectedFileId={activeFileId}
                  onNavigateToFolder={(folderId) => {
                    if (!folderId) {
                      handleSelectRoot();
                      return;
                    }
                    handleSelectFolder(folderId);
                  }}
                  showFiles
                  enableFileDrag={canUpdate}
                  onOpenFile={handleSelectFile}
                  onMoveItem={(item, targetFolderId) => {
                    void moveSelectedItemsToFolder(item, targetFolderId);
                  }}
                  sortKey={sortKey}
                  showCheckboxes
                  selectedItemIds={selectedItemIds}
                  onToggleSelection={handleToggleSelection}
                  onToggleRootSelection={handleToggleRootSelection}
                  renderContextMenu={(item, trigger) => (
                    <FirmFileContextMenu
                      items={
                        selectedItemIds.has(item.id) ? selectedFiles : [item]
                      }
                      trigger={trigger}
                      canCreate={canCreate}
                      canUpdate={canUpdate}
                      canDelete={canDelete}
                      canDocumentEdit={canDocumentEdit}
                      onEdit={handleEditItem}
                      onDelete={(items) => {
                        if (items.length === 1) {
                          setItemToDelete(items[0]);
                        } else {
                          setBulkItemsToDelete(items);
                        }
                      }}
                      onDownload={(items) => {
                        void handleDownloadFiles(items);
                      }}
                      onCreateSubfolder={handleCreateSubfolder}
                      onEditDocument={(file) => {
                        if (
                          getFirmFileExtension(file).toLowerCase() === "docx"
                        ) {
                          setDocxEditorFile(file);
                        } else {
                          setPdfEditorFile(file);
                        }
                      }}
                    />
                  )}
                />
                {treeItems.length === 0 && query.trim() && (
                  <div className="px-3 py-4">
                    <Typography className="text-muted-foreground text-sm">
                      No matching files or folders.
                    </Typography>
                  </div>
                )}
              </ScrollHint>
            )}
            {showBulkActions && (
              <FileBulkActions
                selectedCount={selectedFiles.length}
                canDelete={canDelete}
                onClear={handleClearSelection}
                onDownload={() => {
                  void handleDownloadFiles(selectedFiles);
                }}
                onDelete={() => {
                  setBulkItemsToDelete(selectedFiles);
                }}
              />
            )}
          </div>

          <div
            className="bg-card relative isolate flex min-w-0 flex-1 flex-col"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDropFiles}
          >
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="flex min-w-0 items-center gap-2">
                <HeaderIcon
                  className={cn(
                    "h-5 w-5 shrink-0",
                    selectedFile ? "text-blue-500" : "text-amber-500",
                  )}
                />
                <FileVersionTitle setContainer={setVersionControlsContainer}>
                  <Typography
                    weight="semibold"
                    className="max-w-[520px] truncate"
                  >
                    {headerTitle}
                  </Typography>
                </FileVersionTitle>

                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  disabled={!canEditSelectedItem}
                  onClick={handleOpenEditSlideover}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  disabled={!canDownloadSelectedItem}
                  onClick={() => {
                    void handleDownloadSelectedItem();
                  }}
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  disabled={!canDeleteSelectedItem}
                  onClick={() => {
                    if (selectedItem) {
                      setItemToDelete(selectedItem);
                    }
                  }}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex h-full flex-col overflow-hidden">
              {isLoading ? (
                <div className="flex h-full flex-1 items-center justify-center p-6">
                  <LoadingSpinner />
                </div>
              ) : previewFile ? (
                <div className="flex h-full min-h-0 flex-col p-6">
                  <FileViewer
                    file={previewFile}
                    onVersionChange={setPreviewVersion}
                    versionControlsContainer={versionControlsContainer}
                    fileType="firm-file"
                    showDownload={false}
                    showTitle={false}
                    footerClassName="hidden"
                    viewerClassName="bg-card items-stretch justify-stretch"
                    wrapperClassName="bg-card rounded-none shadow-none"
                    compactDocxPreview
                    showDocxModeToggle={isSelectedDocx}
                    canEditDocx={canEditSelectedDocx}
                    onDocxEdit={handleOpenDocxEditor}
                    showEditOverlay={isSelectedPdf}
                    canEditOverlay={canEditSelectedPdf}
                    onEdit={
                      canEditSelectedPdf ? handleOpenPdfEditor : undefined
                    }
                  />
                </div>
              ) : (
                <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
                  {selectedItem?.isFolder || !selectedItem ? (
                    <>
                      <button
                        type="button"
                        className={cn(
                          "group flex flex-col items-center gap-3 rounded-lg px-8 py-6 transition",
                          canUploadToCurrentLocation
                            ? "hover:bg-muted/30 hover:shadow-sm"
                            : "cursor-not-allowed opacity-50",
                        )}
                        disabled={!canUploadToCurrentLocation}
                        onClick={() => {
                          openCreateSlideover("file", {
                            autoOpenUpload: true,
                          });
                        }}
                      >
                        <div className="relative">
                          <Folder className="text-muted-foreground/40 h-16 w-16" />
                          <div className="bg-primary text-primary-foreground absolute -right-1 -bottom-1 flex h-7 w-7 items-center justify-center rounded-full">
                            <Plus className="h-4 w-4" />
                          </div>
                        </div>
                        <Typography weight="semibold">
                          {`Add file to ${headerTitle}`}
                        </Typography>
                        <Typography className="text-muted-foreground text-sm">
                          Click to upload a file
                        </Typography>
                      </button>
                    </>
                  ) : (
                    <>
                      <Typography weight="semibold">
                        Preview not available.
                      </Typography>
                      <Typography className="text-muted-foreground text-sm">
                        This file does not have a preview link.
                      </Typography>
                    </>
                  )}
                </div>
              )}
            </div>

            {showDropOverlay && (
              <div className="border-primary/70 bg-background/70 text-primary/90 pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 border-2 border-dashed backdrop-blur-sm">
                {isUploadingFiles ? (
                  <>
                    <LoadingSpinner className="text-primary h-6 w-6" />
                    <Typography weight="semibold">
                      Uploading files...
                    </Typography>
                    <Typography className="text-sm">
                      This may take a moment.
                    </Typography>
                  </>
                ) : (
                  <>
                    <FilesIcon className="text-primary h-8 w-8" />
                    <Typography weight="semibold">
                      Drop files to upload
                    </Typography>
                    <Typography className="text-sm">
                      Release to import into this folder.
                    </Typography>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <AddOrEditFirmFileModal
        open={isCreateSlideoverOpen}
        setOpen={handleSetCreateOpen}
        currentFolderId={currentFolderId}
        isFolder={createMode === "folder"}
        autoOpenUpload={autoOpenNewFileUpload}
        existingFiles={files}
      />
      <AddOrEditFirmFileModal
        open={isEditSlideoverOpen}
        setOpen={(open) => {
          setIsEditSlideoverOpen(open);
          if (!open) {
            setItemBeingEdited(undefined);
          }
        }}
        editing
        activeFile={itemBeingEdited}
        existingFiles={files}
      />
      <CreateDocxFirmFileModal
        open={isCreateDocxOpen}
        setOpen={setIsCreateDocxOpen}
        currentFolderId={currentFolderId}
        existingFileNames={getFirmFileDisplayNamesInFolder(
          files,
          currentFolderId,
        )}
      />
      {docxEditorFile && (
        <EditDocxFirmFileModal
          open={!!docxEditorFile}
          setOpen={(open) => {
            if (!open) {
              setDocxEditorFile(null);
            }
          }}
          sourceFile={docxEditorFile}
        />
      )}
      {pdfEditorFile && (
        <EditPdfFirmFileModal
          open={!!pdfEditorFile}
          setOpen={(open) => {
            if (!open) {
              setPdfEditorFile(null);
            }
          }}
          sourceFile={pdfEditorFile}
        />
      )}
      <ActionConfirmationModal
        open={!!itemToDelete}
        setOpen={() => {
          setItemToDelete(undefined);
        }}
        title={`Delete ${itemToDelete?.isFolder ? "folder" : "file"}`}
        description={
          itemToDelete?.isFolder
            ? "Are you sure you want to delete this folder? If it has contents, they will be deleted as well. This action cannot be undone."
            : "Are you sure you want to delete this file? This action cannot be undone."
        }
        afterActionFn={async () => {
          if (!itemToDelete) return;

          await deleteFirmFile.mutateAsync({
            id: itemToDelete.id,
            isFolder: itemToDelete.isFolder,
          });

          if (itemToDelete.id === activeFileId) {
            setActiveFileId(null);
          }

          if (itemToDelete.id === currentFolderId) {
            setCurrentFolderId(itemToDelete.idLocation || "");
          }

          setItemToDelete(undefined);
        }}
      />
      <ActionConfirmationModal
        open={!!bulkItemsToDelete}
        setOpen={() => {
          setBulkItemsToDelete(null);
        }}
        title={`Delete ${bulkItemsToDelete?.length ?? 0} files`}
        description="Are you sure you want to delete the selected files? This action cannot be undone."
        afterActionFn={handleConfirmBulkDelete}
      />
    </>
  );
}

export function FirmFilesView() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <FirmFilesViewDangerous />
    </ErrorBoundary>
  );
}
